import type { ButtonHTMLAttributes, ReactNode } from 'react'

/**
 * El botón de la app.
 *
 * **Regla dura: una sola `primary` por tarjeta o por vista.** Si hay dos, no hay
 * ninguna: el ojo no sabe cuál es la acción esperada y las dos se vuelven ruido
 * teal. Cuando aparece la tentación de una segunda, casi siempre lo que hace
 * falta es que la otra sea `secondary`.
 *
 * Las variantes, por lo que significan y no por cómo se ven:
 *
 *   primary    Lo que la persona vino a hacer en esta pantalla.
 *   secondary  Alternativa legítima al lado de la principal: "Rechazar",
 *              "Cancelar", "Hoy".
 *   ghost      Navegación y acciones menores. No compite con nada.
 *   danger     Destruye o libera algo que cuesta recuperar.
 *
 * Un detalle que no es cosmético: `disabled` baja la opacidad **y** cambia el
 * cursor. Un botón teal apagado sin más señal se lee como "botón feo", no como
 * "todavía no". Cuando se pueda, es mejor no mostrar el botón que mostrarlo
 * deshabilitado.
 */

export type VarianteBoton = 'primary' | 'secondary' | 'ghost' | 'danger'
export type TamanoBoton = 'md' | 'sm'

const VARIANTES: Record<VarianteBoton, string> = {
  primary: 'bg-teal-700 text-white hover:bg-teal-800 focus-visible:outline-teal-700',
  secondary:
    'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus-visible:outline-slate-400',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-slate-400',
  danger:
    'border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 focus-visible:outline-rose-400',
}

const TAMANOS: Record<TamanoBoton, string> = {
  md: 'h-9 px-4 text-[14px]',
  sm: 'h-8 px-3 text-[13px]',
}

export interface BotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBoton
  tamano?: TamanoBoton
  children: ReactNode
}

export function Boton({
  variante = 'secondary',
  tamano = 'md',
  className = '',
  type = 'button',
  ...props
}: BotonProps) {
  return (
    <button
      type={type}
      // `w-auto` explícito: dentro de un flex, un botón sin ancho propio se
      // estira hasta donde llegue el contenedor. Así fue como "Confirmar"
      // terminó midiendo 1770 px.
      className={`inline-flex w-auto shrink-0 items-center justify-center gap-2 rounded-lg font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${TAMANOS[tamano]} ${VARIANTES[variante]} ${className}`}
      {...props}
    />
  )
}
